import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { motion as Motion } from 'framer-motion';
import { submitStoryReview } from '../../api/stories';
import { useDelight } from '../../contexts/DelightContext';
import { useLearner } from '../../hooks/useLearner';
import { getRandomBuddyAsset, SLOT } from '../../lib/buddyManifest';
import { SPRING_SNAPPY } from '../../constants/motion';
import s from './ReviewSummary.module.css';

const isCjk = (ch) => ch && /[一-鿿㐀-䶿]/.test(ch);

// Dedupe story characters by display_state, CJK only — same grouping the
// CelebrationPage uses (From Practice = practicing, New Today = new_introduction).
function groupByState(characters, state) {
  const seen = new Set();
  return (characters || []).filter((c) => {
    if (c.display_state !== state || !isCjk(c.char)) return false;
    if (seen.has(c.char)) return false;
    seen.add(c.char);
    return true;
  });
}

export default function ReviewSummary({ story, hardCharIds, onPractice, onHome }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueDelight } = useDelight();
  const { data: activeLearner } = useLearner();

  const [status, setStatus] = useState('submitting'); // submitting | done | error
  const [errorMsg, setErrorMsg] = useState(null);
  const submittedRef = useRef(false);

  // The learner's buddy in a CELEBRATE pose — the visual hero. Picked once.
  const [buddySrc] = useState(() =>
    getRandomBuddyAsset(activeLearner?.buddy?.slug, SLOT.CELEBRATE)
  );

  const practicingChars = useMemo(
    () => groupByState(story?.characters, 'practicing'),
    [story]
  );
  const newChars = useMemo(
    () => groupByState(story?.characters, 'new_introduction'),
    [story]
  );

  async function runSubmit() {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setStatus('submitting');
    setErrorMsg(null);
    try {
      const result = await submitStoryReview(story?.id, {
        hardCharacterIds: hardCharIds || [],
      });
      if (result?.progression) enqueueDelight(result.progression);
      queryClient.invalidateQueries({ queryKey: ['childStories'] });
      queryClient.invalidateQueries({ queryKey: ['childCharacters'] });
      queryClient.invalidateQueries({ queryKey: ['learnerProfile'] });
      queryClient.invalidateQueries({ queryKey: ['librarySeries'] });
      queryClient.invalidateQueries({ queryKey: ['librarySeriesDetail'] });
      setStatus('done');
    } catch (err) {
      submittedRef.current = false; // allow retry
      if (import.meta.env.DEV) console.error('Review submission failed:', err);
      setErrorMsg(err?.message || t('review.submitError', 'Failed to submit review.'));
      setStatus('error');
    }
  }

  useEffect(() => {
    runSubmit();
    // submit-once-on-mount; runSubmit is guarded by submittedRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Motion.div
      className={s.wrap}
      style={{ background: 'var(--color-storybook-cream)' }}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={SPRING_SNAPPY}
    >
      <div className={s.buddyArea}>
        {buddySrc && (
          <img
            src={buddySrc}
            alt=""
            aria-hidden="true"
            className={s.heroBuddy}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>

      <h1 className={s.title}>{t('review.completionTitle', 'Review Complete!')}</h1>
      <p className={s.subtitle}>{t('review.completionBody')}</p>

      {(practicingChars.length > 0 || newChars.length > 0) && (
        <div className={s.breakdown}>
          {practicingChars.length > 0 && (
            <div className={s.bucket}>
              <p className={`${s.bucketLabel} ${s.bucketLabelGreen}`}>
                {t('celebration.fromPractice', 'From Practice')}
              </p>
              <div className={s.charRow}>
                {practicingChars.map((c) => (
                  <span key={c.id} className={s.charChip}>{c.char}</span>
                ))}
              </div>
            </div>
          )}
          {newChars.length > 0 && (
            <div className={s.bucket}>
              <p className={`${s.bucketLabel} ${s.bucketLabelOrange}`}>
                {t('celebration.newToday', 'New Today')}
              </p>
              <div className={s.charRow}>
                {newChars.map((c) => (
                  <span key={c.id} className={s.charChip}>{c.char}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {status === 'error' && (
        <div className={s.errorBlock} role="alert">
          <p className={s.errorText}>{errorMsg}</p>
          <button type="button" className={s.retryBtn} onClick={runSubmit}>
            {t('common.retry', 'Try again')}
          </button>
        </div>
      )}

      <div className={s.buttons}>
        <button
          type="button"
          className={s.btnOutline}
          onClick={onPractice}
          disabled={status !== 'done'}
        >
          {t('practice.title')}
        </button>
        <button
          type="button"
          className={s.btnPrimary}
          onClick={onHome}
        >
          {t('review.backHome')}
        </button>
      </div>
    </Motion.div>
  );
}
